import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { brlPriceRegex, centsToBrlInput, parseBrlToCents } from "@/lib/price";
import type { RawItem, RawItemInput } from "@/types/models";

const rawItemSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  unitLabel: z.string(),
  cost: z
    .string()
    .min(1, "Custo é obrigatório")
    .refine((val) => brlPriceRegex.test(val.trim()), {
      message: "Custo inválido. Use formato 199,90",
    }),
});

export type RawItemFormValues = z.infer<typeof rawItemSchema>;

const defaultValues: RawItemFormValues = {
  name: "",
  unitLabel: "",
  cost: "",
};

function toRawItemInput(values: RawItemFormValues, id?: string): RawItemInput {
  return {
    id,
    name: values.name.trim(),
    unitLabel: values.unitLabel.trim() || null,
    costCents: parseBrlToCents(values.cost),
  };
}

export interface RawItemFormProps {
  selectedRawItem?: RawItem | null;
  saving?: boolean;
  onSubmit: (input: RawItemInput) => void;
  onCancelEdit?: () => void;
}

export function RawItemForm({
  selectedRawItem,
  saving = false,
  onSubmit,
  onCancelEdit,
}: RawItemFormProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const isEditMode = Boolean(selectedRawItem);

  const form = useForm<RawItemFormValues>({
    resolver: zodResolver(rawItemSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!selectedRawItem) {
      form.reset(defaultValues);
      return;
    }

    form.reset({
      name: selectedRawItem.name,
      unitLabel: selectedRawItem.unitLabel ?? "",
      cost: centsToBrlInput(selectedRawItem.costCents),
    });
  }, [selectedRawItem, form]);

  const handleSubmit = (values: RawItemFormValues) => {
    setActionError(null);
    onSubmit(toRawItemInput(values, selectedRawItem?.id));
  };

  return (
    <section className="border border-primary-200 dark:border-primary-800 p-8">
      <h2 className="text-xl font-light text-primary-900 dark:text-white mb-6">
        {isEditMode ? "Editar insumo" : "Novo insumo"}
      </h2>

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 max-w-md">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: Farinha de trigo" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unitLabel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unidade</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: kg, un, L (opcional)" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custo (R$)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: 12,50" inputMode="decimal" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Salvando…" : isEditMode ? "Salvar alterações" : "Criar insumo"}
            </Button>
            {isEditMode && onCancelEdit && (
              <Button type="button" variant="outline" onClick={onCancelEdit}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </section>
  );
}
